import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { listProjectMembers, getProject } from "services/pmService";

const toText = (value) => String(value ?? "").trim();
const normalizeText = (value) => toText(value).toLowerCase().replace(/\s+/g, " ");

const isTruthy = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const toUniqueIds = (values) =>
  [...new Set(values.map(toText).filter(Boolean))];

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const normalized = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const getUserIds = (user) =>
  toUniqueIds((() => {
    const token = sessionStorage.getItem("authToken");
    const jwt = decodeJwtPayload(token);
    return [
    user?.id,
    user?.userId,
    user?.identityUserId,
    user?.accountId,
    jwt?.sub,
    jwt?.userId,
    jwt?.uid,
    jwt?.identityUserId,
    sessionStorage.getItem("actorId"),
    ];
  })());

const getUserNames = (user) =>
  toUniqueIds((() => {
    const token = sessionStorage.getItem("authToken");
    const jwt = decodeJwtPayload(token);
    return [
      user?.fullName,
      user?.name,
      `${toText(user?.firstName)} ${toText(user?.lastName)}`.trim(),
      user?.email,
      user?.username,
      user?.displayName,
      user?.preferredUsername,
      jwt?.preferred_username,
      jwt?.name,
      jwt?.email,
      jwt?.given_name,
      jwt?.family_name,
    ];
  })()).map(normalizeText);

const matchesUserId = (member, userIds) => {
  if (!member || !Array.isArray(userIds) || userIds.length === 0) return false;
  const candidates = [
    member?.userId,
    member?.user?.id,
    member?.user?.userId,
    member?.identityUserId,
    member?.accountId,
  ];

  const memberIds = toUniqueIds(candidates);
  return memberIds.some((id) => userIds.includes(id));
};

const matchesUserName = (member, userNames) => {
  if (!member || !Array.isArray(userNames) || userNames.length === 0) return false;

  const candidates = [
    member?.fullName,
    member?.name,
    member?.userName,
    member?.email,
    member?.user?.fullName,
    member?.user?.name,
    member?.user?.email,
    member?.createdByName,
    `${toText(member?.firstName)} ${toText(member?.lastName)}`.trim(),
    `${toText(member?.user?.firstName)} ${toText(member?.user?.lastName)}`.trim(),
  ]
    .map(normalizeText)
    .filter(Boolean);

  return candidates.some((name) => userNames.includes(name));
};

const roleLooksAdmin = (role) => {
  if (!role) return false;

  // Only consider it admin if the role code/name is exactly ADMIN or ends with _ADMIN
  // or is specifically designated as an admin role
  const values = [role.code, role.name, role.key, role.slug]
    .map((v) => toText(v).toUpperCase())
    .filter(Boolean);

  // Must match exactly ADMIN or contain _ADMIN suffix (e.g., PROJECT_ADMIN)
  return values.some((v) => v === "ADMIN" || v.endsWith("_ADMIN"));
};

const memberIsAdmin = (member) => {
  if (!member) return false;

  if (
    isTruthy(member.isAdmin) ||
    isTruthy(member.admin) ||
    isTruthy(member.projectAdmin) ||
    isTruthy(member.isProjectAdmin) ||
    isTruthy(member.owner)
  ) {
    return true;
  }

  return roleLooksAdmin(member.role);
};

const projectOwnedByUser = (project, userIds, userNames) => {
  if (!project) return false;

  // Also get JWT to match identity IDs against project owner fields
  const token = sessionStorage.getItem("authToken");
  const jwt = decodeJwtPayload(token);

  const idCandidates = [
    project.ownerId,
    project.createdBy,
    project.createdById,
    project.createdByUserId,
    project.ownerUserId,
    project.creatorId,
    project.owner?.id,
    project.owner?.userId,
    // Also check identity-based IDs that might be stored in owner fields
    jwt?.sub,
    jwt?.userId,
    jwt?.uid,
    jwt?.identityUserId,
  ];

  const projectIds = toUniqueIds(idCandidates);
  const matchesId =
    Array.isArray(userIds) &&
    userIds.length > 0 &&
    projectIds.some((id) => userIds.includes(id));

  if (matchesId) return true;

  const nameCandidates = [
    project.ownerName,
    project.owner,
    project.createdByName,
    project.owner?.name,
    project.createdByUserName,
    // Add JWT name fields that might match owner name
    jwt?.name,
    jwt?.preferred_username,
  ]
    .map(normalizeText)
    .filter(Boolean);

  return (
    Array.isArray(userNames) &&
    userNames.length > 0 &&
    nameCandidates.some((name) => userNames.includes(name))
  );
};

export default function useProjectAccess(projectId, project = null) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [canReadProject, setCanReadProject] = useState(false);
  const userIds = useMemo(() => getUserIds(user), [user]);
  const userNames = useMemo(() => getUserNames(user), [user]);

  const refreshAccess = useCallback(async () => {
    if (!projectId) {
      setMembers([]);
      setAccessError(null);
      setCanReadProject(false);
      return;
    }

    setAccessLoading(true);
    setAccessError(null);

    try {
      const data = await listProjectMembers(projectId);
      setMembers(Array.isArray(data) ? data : []);
      setCanReadProject(true);
    } catch (error) {
      setAccessError(error);
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        setMembers([]);
        try {
          await getProject(projectId);
          setCanReadProject(true);
        } catch {
          setCanReadProject(false);
        }
      } else {
        setCanReadProject(Boolean(project));
      }
    } finally {
      setAccessLoading(false);
    }
  }, [projectId, project]);

  useEffect(() => {
    refreshAccess();
  }, [refreshAccess]);

  const currentMember = useMemo(
    () =>
      members.find(
        (m) => matchesUserId(m, userIds) || matchesUserName(m, userNames)
      ) || null,
    [members, userIds, userNames]
  );

  const owner = useMemo(
    () => projectOwnedByUser(project, userIds, userNames),
    [project, userIds, userNames]
  );

  // canView: user can see the project content (read-only access)
  // This includes members, owners, and non-members who have basic read access
  const canView = owner || Boolean(currentMember) || canReadProject;
  
  // isMember: user is an actual member of the project (can edit, etc.)
  // This does NOT include non-members who can only view
  const isMember = owner || Boolean(currentMember);
  // isAdmin: user is the project owner (only owner can manage team)
  // Note: Members with admin flags are no longer treated as admins for team management
  // Only the project owner can add/remove members and roles
  const isAdmin = owner;

  return {
    members,
    currentMember,
    canView,
    isMember,
    isAdmin,
    accessLoading,
    accessError,
    refreshAccess,
  };
}
